
import { useState, useRef } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6dnrubv53dtlm7j8of0';

const serviceOptions = [
  'Instalación Residencial',
  'Proyecto Industrial',
  'Autoconsumo Empresarial',
  'Baterías de Almacenamiento',
  'Mantenimiento y Reparación',
  'Otro',
];

export default function ContactForm() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    provincia: '',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        body.append(key, value);
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ nombre: '', email: '', telefono: '', servicio: '', provincia: '', mensaje: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="presupuesto" className="py-24 bg-solar-beige relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
        <img
          src="https://public.readdy.ai/ai/img_res/d5dda65970f6012b6c77098c11229a5f.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left info */}
          <div>
            <span className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              Presupuesto sin compromiso
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Solicita Tu <br />Presupuesto Gratuito
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-10 max-w-md">
              Completa el formulario y nuestro equipo de expertos te contactará en menos de 24 horas con una propuesta personalizada para tu proyecto solar.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0A2E1F] flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-[#FFB800] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Respuesta en 24h</h4>
                  <p className="text-gray-500 text-sm">Nuestro equipo te contactará rápidamente con tu presupuesto detallado.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0A2E1F] flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-[#FFB800] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Sin compromiso</h4>
                  <p className="text-gray-500 text-sm">Estudio técnico y presupuesto totalmente gratuitos y sin obligación.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0A2E1F] flex items-center justify-center flex-shrink-0">
                  <i className="ri-customer-service-2-line text-[#FFB800] text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">Asesoramiento experto</h4>
                  <p className="text-gray-500 text-sm">Técnicos certificados analizarán tu caso para ofrecerte la mejor solución.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <a href="tel:+34680254236" className="flex items-center gap-2 text-[#0A2E1F] font-semibold text-sm hover:text-[#FF6B35] transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-[#0A2E1F] flex items-center justify-center">
                  <i className="ri-phone-line text-white text-lg"></i>
                </div>
                680254236
              </a>
              <a href="mailto:jyafotovoltaica@gmail.com" className="flex items-center gap-2 text-[#0A2E1F] font-semibold text-sm hover:text-[#FF6B35] transition-colors cursor-pointer">
                <div className="w-10 h-10 rounded-full bg-[#0A2E1F] flex items-center justify-center">
                  <i className="ri-mail-line text-white text-lg"></i>
                </div>
                jyafotovoltaica@gmail.com
              </a>
            </div>
          </div>

          {/* Right form */}
          <div className="bg-white rounded-3xl shadow-xl shadow-black/5 p-8 md:p-10">
            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-green-600 text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">¡Solicitud Enviada!</h3>
                <p className="text-gray-600 text-sm mb-8 max-w-sm mx-auto">
                  Hemos recibido tu solicitud correctamente. Nuestro equipo se pondrá en contacto contigo en menos de 24 horas.
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="bg-[#0A2E1F] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#FFB800] hover:text-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Enviar otra solicitud
                </button>
              </div>
            ) : (
              <form
                ref={formRef}
                id="presupuesto-form"
                data-readdy-form
                onSubmit={handleSubmit}
              >
                <h3 className="text-xl font-bold text-gray-900 mb-1">Datos de contacto</h3>
                <p className="text-gray-500 text-sm mb-6">Todos los campos marcados con * son obligatorios</p>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1.5">Nombre completo *</label>
                    <input
                      id="nombre"
                      name="nombre"
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={handleChange}
                      placeholder="Tu nombre y apellidos"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="tu@email.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-1.5">Teléfono *</label>
                    <input
                      id="telefono"
                      name="telefono"
                      type="tel"
                      required
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder="+34 600 000 000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="provincia" className="block text-sm font-medium text-gray-700 mb-1.5">Provincia *</label>
                    <input
                      id="provincia"
                      name="provincia"
                      type="text"
                      required
                      value={formData.provincia}
                      onChange={handleChange}
                      placeholder="Ej: Madrid, Barcelona..."
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="servicio" className="block text-sm font-medium text-gray-700 mb-1.5">Tipo de servicio *</label>
                  <select
                    id="servicio"
                    name="servicio"
                    required
                    value={formData.servicio}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all appearance-none bg-white cursor-pointer"
                  >
                    <option value="" disabled>Selecciona un servicio</option>
                    {serviceOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-1.5">Mensaje</label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    rows={4}
                    maxLength={500}
                    value={formData.mensaje}
                    onChange={handleChange}
                    placeholder="Cuéntanos sobre tu proyecto: tipo de vivienda, consumo mensual, superficie disponible..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{formData.mensaje.length}/500</p>
                </div>

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    Ha ocurrido un error. Por favor, inténtalo de nuevo.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#FF6B35] text-white py-3.5 rounded-lg text-sm font-semibold hover:bg-[#FFB800] hover:text-gray-900 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      Solicitar Presupuesto Gratuito
                      <i className="ri-arrow-right-line text-lg"></i>
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center mt-4">
                  Al enviar aceptas nuestra <a href="#" className="underline hover:text-[#FF6B35] cursor-pointer" rel="nofollow">política de privacidad</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
